import { useState, useCallback, ReactElement, ChangeEvent } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  TextField,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { AdminPermission, UserRole } from '@localstack/types';
import { ProgressButton } from '@localstack/ui';

import { useAuthProvider } from '~/hooks/useAuthProvider';

type Props = {
  className?: string;
  loading: boolean;
  searching: boolean;
  searchedOrganizations: [string, string][];
  onSearchOrganization: (query: string) => unknown;
  onCreateMember: (orgId: string, role: UserRole) => unknown;
};

export const MemberForm = ({
  className,
  loading,
  searching,
  searchedOrganizations,
  onSearchOrganization,
  onCreateMember,
}: Props): ReactElement => {
  const { can } = useAuthProvider();
  const [org, setOrg] = useState<[string, string]>(['', '']);
  const [orgQuery, setOrgQuery] = useState<Optional<string>>(null);
  const [role, setRole] = useState<Optional<UserRole>>(null);

  const handleOnCreateMember = useCallback(() => {
    if (!org[0] || !role) return;
    onCreateMember(org[0], role);
  }, [org, role, onCreateMember]);

  // lets an admin bypass search and add a membership by raw org ID directly (e.g. if search is down or not yet materialized)
  const augmentedSearchedOrganizations: [string, string][] = [
    ...(orgQuery ? [[orgQuery, orgQuery] as [string, string]] : []),
    ...searchedOrganizations,
  ];

  const isManualIdOption = (option: [string, string], index: number) => index === 0 && option[0] === orgQuery;

  return (
    <Card className={className} variant="outlined">
      <CardHeader title="Add new Membership" />
      <CardContent>
        <Box>
          <Autocomplete
            options={augmentedSearchedOrganizations}
            getOptionLabel={(option: [string, string]) => option[1]}
            renderOption={(props, option, state) =>
              isManualIdOption(option, state.index) ? (
                <Box component="li" {...props} sx={{ display: 'block' }}>
                  <Alert severity="warning" sx={{ width: '100%' }}>
                    Add by raw ID <code>{option[0]}</code> - only use if you already know this is a valid organization
                    ID
                  </Alert>
                </Box>
              ) : (
                <Box component="li" {...props}>
                  {option[1]}
                </Box>
              )
            }
            onChange={(_, value) => setOrg((value ?? ['', '']) as [string, string])}
            value={org}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Organization"
                variant="outlined"
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setOrgQuery(e.target.value);
                  onSearchOrganization(e.target.value);
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {searching ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>
        <Box mt={3}>
          <FormControl variant="outlined" fullWidth required>
            <InputLabel>Member Role</InputLabel>
            <Select
              variant="standard"
              value={role ?? ''}
              label="Member Role"
              onChange={({ target }) => setRole(target.value as UserRole)}
            >
              <MenuItem value={UserRole.ADMIN}>Admin</MenuItem>
              <MenuItem value={UserRole.MEMBER}>Member</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </CardContent>
      <CardActions>
        <ProgressButton
          color="primary"
          variant="contained"
          size="small"
          loading={loading}
          disabled={!org || !role || searching || !can(AdminPermission.USERS_WRITE)}
          onClick={handleOnCreateMember}
        >
          Add
        </ProgressButton>
      </CardActions>
    </Card>
  );
};
